import React, { useState } from "react";
import { profile } from "../mock";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { toast } from "sonner";
import { ArrowUpRight, Mail, Linkedin, Send, Download } from "lucide-react";
import { useRole } from "../context/RoleContext";

const WEB3FORMS_ENDPOINT = "https://api.web3forms.com/submit";
const WEB3FORMS_ACCESS_KEY = "7fb49b66-adf0-408d-9f63-6cc93049832a";

const submitToWeb3Forms = async (form, roleKey) => {
  const body = new FormData();
  body.append("access_key", WEB3FORMS_ACCESS_KEY);
  body.append("subject", `[${roleKey.toUpperCase()} site] Portfolio enquiry — ${form.intent} — ${form.name}`);
  body.append("from_name", form.name);
  body.append("name", form.name);
  body.append("email", form.email);
  body.append("intent", form.intent);
  body.append("message", form.message);
  body.append("botcheck", "");

  const response = await fetch(WEB3FORMS_ENDPOINT, { method: "POST", body });
  const data = await response.json();
  if (!data.success) throw new Error(data.message || "Submission failed");
  return data;
};

const Contact = () => {
  const { key: roleKey, contact } = useRole();
  const INTENT_OPTIONS = contact.intents;
  const DEFAULT_FORM = { name: "", email: "", intent: contact.intents[0], message: "" };
  const [form, setForm] = useState(DEFAULT_FORM);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Need your name, email and a quick note.");
      return;
    }
    setSubmitting(true);
    try {
      await submitToWeb3Forms(form, roleKey);
      toast.success("Sent. I'll reply within 48 hours.");
      setForm(DEFAULT_FORM);
    } catch (err) {
      console.error("Contact form failed:", err);
      toast.error("Couldn't send. Email tansym123@gmail.com directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-pad border-t border-white/5 relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 460px at 80% 0%, rgba(189, 195, 199,0.12), transparent 60%), radial-gradient(800px 460px at 20% 100%, rgba(236, 240, 241,0.12), transparent 60%)"
        }}
      />

      <div className="relative container-x">
        {/* Massive type heading */}
        <div className="mb-12 md:mb-16">
          <div className="eyebrow mb-3">§ Let's talk</div>
          <h2
            data-testid="contact-headline"
            className="display text-[64px] sm:text-[96px] md:text-[140px] lg:text-[180px] leading-[0.88] tracking-[-0.05em] text-[#FFF3E9]"
          >
            <span className="block">SAY</span>
            <span className="block">
              <span className="serif-italic" style={{ color: "#FF8A5B" }}>hi</span>
              <span className="text-[#FFD2A6]">.</span>
            </span>
          </h2>
          <p data-testid="contact-blurb" className="mt-6 max-w-xl text-[15px] md:text-[16px] text-[#B8B8BC] leading-relaxed">
            {contact.blurb}
          </p>
        </div>

        <div className="grid md:grid-cols-12 gap-10 md:gap-16">
          {/* Direct lanes */}
          <div className="md:col-span-5 space-y-3">
            <a
              href={`mailto:${profile.email}`}
              data-testid="contact-email-link"
              className="group flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border border-white/10 hover:border-[#FF8A5B]/50 transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#FF8A5B", color: "#121826" }}>
                  <Mail className="w-4 h-4" />
                </span>
                <span>
                  <span className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E]">Email</span>
                  <span className="block text-[14px] text-[#EDEDED]">{profile.email}</span>
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4 text-[#8B7B6E] group-hover:text-[#FF8A5B]" />
            </a>

            <a
              href={profile.linkedin}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-linkedin-link"
              className="group flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border border-white/10 hover:border-[#FFF3E9]/50 transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#FFD2A6", color: "#121826" }}>
                  <Linkedin className="w-4 h-4" />
                </span>
                <span>
                  <span className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E]">LinkedIn</span>
                  <span className="block text-[14px] text-[#EDEDED]">/in/tanmay-a-aa4338108</span>
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4 text-[#8B7B6E] group-hover:text-[#FFF3E9]" />
            </a>

            <a
              href={profile.cv}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-cv-link"
              className="group flex items-center justify-between gap-3 px-5 py-4 rounded-2xl border border-white/10 hover:border-[#FFD2A6]/50 transition-colors"
            >
              <span className="flex items-center gap-3">
                <span className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: "#FFF3E9", color: "#121826" }}>
                  <Download className="w-4 h-4" />
                </span>
                <span>
                  <span className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E]">CV</span>
                  <span className="block text-[14px] text-[#EDEDED]">Tanmay_Agarwal_CV_2027_Final.pdf</span>
                </span>
              </span>
              <ArrowUpRight className="w-4 h-4 text-[#8B7B6E] group-hover:text-[#FFD2A6]" />
            </a>

            <p className="pt-4 text-[12px] text-[#8B7B6E] leading-relaxed">
              {contact.tip}
            </p>
          </div>

          {/* Form */}
          <form
            onSubmit={handleSubmit}
            data-testid="contact-form"
            className="md:col-span-7 space-y-5 p-6 md:p-8 rounded-3xl border border-white/10 bg-white/[0.02]"
          >
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label htmlFor="name" className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E] mb-2">
                  Your name
                </label>
                <Input
                  id="name"
                  name="name"
                  data-testid="contact-input-name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="Real name only :)"
                  className="bg-transparent border-0 border-b border-white/15 rounded-none text-[16px] text-[#EDEDED] placeholder:text-[#5A5A62] h-11 px-0 focus-visible:border-[#FF8A5B] focus-visible:ring-0"
                />
              </div>
              <div>
                <label htmlFor="email" className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E] mb-2">
                  Email
                </label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  data-testid="contact-input-email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder={contact.emailPlaceholder}
                  className="bg-transparent border-0 border-b border-white/15 rounded-none text-[16px] text-[#EDEDED] placeholder:text-[#5A5A62] h-11 px-0 focus-visible:border-[#FFD2A6] focus-visible:ring-0"
                />
              </div>
            </div>

            <div>
              <span className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E] mb-3">
                Reason
              </span>
              <div className="flex flex-wrap gap-2">
                {INTENT_OPTIONS.map((opt) => {
                  const active = form.intent === opt;
                  return (
                    <button
                      key={opt}
                      type="button"
                      data-testid={`contact-intent-${opt.replace(/\s+/g, "-").toLowerCase()}`}
                      onClick={() => setForm((p) => ({ ...p, intent: opt }))}
                      className={`sticker transition-all ${active ? "" : "tilt-l-soft"}`}
                      style={
                        active
                          ? { background: "#FF8A5B", color: "#121826" }
                          : { background: "transparent", color: "#A1A1A6", border: "1px solid rgba(255,255,255,0.15)" }
                      }
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label htmlFor="message" className="block mono text-[10px] tracking-widest uppercase text-[#8B7B6E] mb-2">
                Note
              </label>
              <Textarea
                id="message"
                name="message"
                data-testid="contact-input-message"
                value={form.message}
                onChange={handleChange}
                rows={4}
                placeholder="What you're working on, in 3 lines."
                className="bg-transparent border-0 border-b border-white/15 rounded-none text-[16px] text-[#EDEDED] placeholder:text-[#5A5A62] px-0 resize-none focus-visible:border-[#FF8A5B] focus-visible:ring-0"
              />
            </div>

            <div className="flex items-center justify-between pt-2">
              <p className="mono text-[10px] tracking-widest uppercase text-[#8B7B6E]">
                ↩ Powered by Web3Forms
              </p>
              <Button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit-button"
                className="h-11 px-6 rounded-full text-[13px] font-bold gap-2 hover:opacity-95"
                style={{ background: "linear-gradient(135deg, #FF8A5B 0%, #FFD2A6 100%)", color: "#121826" }}
              >
                {submitting ? "Sending…" : "Send it"}
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default Contact;
